<template>
	<div>
		<v-data-table
			class="receipt-table"
			:headers="headers_local"
			:items="items_local"
			:page.sync="page"
			:items-per-page="countPerpage"
			:hide-default-header="!$vuetify.breakpoint.smAndDown"
			hide-default-footer
			disable-sort
			@click:row="onRowClick()"
		>
			<template v-slot:top>
				<div class="table-head" v-if="items_local.length !== 0">
					Showing&nbsp;&nbsp;
					<span>{{ (page-1) * countPerpage + 1}}-{{ page * countPerpage > items_local.length ? items_local.length : page * countPerpage }}</span>
					&nbsp;&nbsp;of&nbsp;&nbsp;
					<span>{{ items_local.length }}</span>
					&nbsp;&nbsp;{{ titleText }}
				</div>
				<div class="table-head" v-else>
					Showing&nbsp;&nbsp;
					<span>{{ page * countPerpage > items_local.length ? items_local.length : page * countPerpage }}</span>
					&nbsp;&nbsp;of&nbsp;&nbsp;
					<span>{{ items_local.length }}</span>
					&nbsp;&nbsp;{{ titleText }}
				</div>
			</template>
			<template v-if="!$vuetify.breakpoint.smAndDown" v-slot:header="{ props: { headers } }">
				<thead>
					<v-draggable v-model="headers_local" tag="tr">
						<th 
							v-for="item in headers" 
							:key="item.value" 
							class="header-th" 
							@mouseover="isHover = item.value"
							@mouseout="isHover = ''">
							<div 
								:class="`header-item ${item.type === 'sort' || item.type === 'file' ? 'pl-3': ''}`" 
								:style="['currency', 'value', 'tax_amount', 'amount', 'markup_perc', 'markup_value'].includes(item.value) ? 'justify-content: center':''"
								v-if="item.showable">
								<div v-if="item.type === 'option'">
									<v-checkbox
										v-if="currentPageSelectableItems.length !== 0"
										color="info"
										:value="allSelect"
										:disabled="currentPageSelectableItems.length === 0 || !privileges.includes(sec)"
										:indeterminate="indeterminate"
										@change="onSelectAllChange($event)"
									></v-checkbox>
								</div>
								<div class="header-item-sub">
									<div class="header-item-text">{{ item.text }}</div>
									<SortUpDown 
										v-if="item.type === 'sort'" 
										class="icon icon-sort-up-down" 
										@click="onSort(item.value)" 
										:selected="isHover === item.value" 
										:selected_color="color"/>
								</div>
								<v-menu offset-y nudge-left="200" >
									<template v-slot:activator="{ on, attrs }">
										<div class="icon-wrap" v-bind="attrs" v-on="on">
											<ShowSearch 
												class="icon" 
												v-if="item.searchable" 
												:selected="isHover === item.value" 
												:selected_color="color"/>
										</div>
									</template>
									<div class="search-box">
										<v-text-field
											placeholder="Search Title..."
											outlined
											dense
											hide-details="auto"
											color="#00AAFD" 
											:value="current_key === item.value ? searchValue : ''"
											@input="searchValue = $event"
											@click.stop
										/>
										<div class="search-box-button">
											<div @click.stop="onClean">CLEAR</div>
											<div @click="onSearch(item.value)">SEARCH</div>
										</div>
									</div>
								</v-menu>
								<v-menu offset-y nudge-left="80" min-width="100px" :close-on-content-click="false">
									<template v-slot:activator="{ on, attrs }">
										<div class="icon-wrap" v-bind="attrs" v-on="on">
											<FirstList 
												v-if="item.value === 'currency'" 
												class="icon icon-sort-up-down" 
												:selected="isHover === item.value" 
												:selected_color="color"/>
										</div>
									</template>
									<div v-for="currency in currencies" :key="currency.id">
										<v-checkbox
											class="filter-box mt-0"
											v-model="select_currency"
											:label="currency.symbol"
											:value="currency.id"
											color="info"
											dense
											hide-details>
										</v-checkbox>
									</div>
								</v-menu>
							</div>
						</th>
					</v-draggable> 
				</thead>
			</template>
			<template v-slot:[`item.select`]="{ item }">		
				<v-checkbox
					v-if="!item.is_used"
					v-model="item.select"
					:disabled="item.is_used || !privileges.includes(sec)"
					@click.native.stop
					color="info"
				></v-checkbox>	
			</template>
			<template v-slot:[`item.date`]="{ item }">
				<DatePicker
					v-if="edit_control.edit_date_visible === item.id"
					class="date-picker-cell"
					:color="color" 
					:isOutlined="true" 
					:suggestionText="'eg, 120322'"
					:dateInit="item.date"
					@date="onUpdate(edit_type.date, item.id, $event)"
				/>		
				<div
					v-else
					:class="`text ${item.is_used ? 'text-disable':(privileges.includes(sec) ? 'text-box-black text-able':'text-able')}`"
					@click.stop="onEditVisible(edit_type.date, item)">
					{{ formatDate(item.date) }}
				</div>
			</template>
			<template v-slot:[`item.currency`]="{ item }">								
				<v-select
					v-if="edit_control.edit_currency_visible === item.id"
					class="currency-select-cell"
					:items="currencies"
					item-text="code"
					item-value="id"
					dense
					hide-details
					outlined
					:color="color"
					@change="onUpdate(edit_type.currency, item.id, $event)"
					@click.stop
				/>
				<div 
					v-else
					style="text-align: center;" 
					:class="`text ${item.is_used ? 'text-disable':(privileges.includes(sec) ? 'text-box-black text-able':'text-able')}`"
					@click.stop="onEditVisible(edit_type.currency, item)">
					{{ item.currency.symbol }}
				</div>
			</template>
			<template v-slot:[`item.value`]="{ item }">	
				<v-text-field
					v-if="edit_control.edit_value_visible === item.id"
					v-model="item.value"
					:color="color"
					type="number"
					class="pl-2 amount-input-cell"
					@change="onUpdate(edit_type.value, item.id, $event)"
					@click.stop
					dense
					hide-details
					outlined
					min="0"
					oninput="if(this.value < 0) this.value = 0;"
				/>
				<div 
					v-else
					class="numeric-cell"
					:class="`text ${item.is_used ? 'text-disable' : item.value === 0 ? (privileges.includes(sec) ? 'text-box-red text-able':'text-able'):(privileges.includes(sec) ? 'text-box-black text-able':'text-able')}`" 
					@click.stop="onEditVisible(edit_type.value, item)">
					{{ formatNumber(item.value) }}
				</div>		
			</template>
			<template v-slot:[`item.tax_amount`]="{ item }">
				<div class="numeric-cell" :class="`text ${item.is_used ? 'text-disable' : 'text-able'}`">
					{{ formatNumber(Number(item.tax_amount) || 0) }}
				</div>
			</template>
			<template v-slot:[`item.amount`]="{ item }">
				<div class="numeric-cell" :class="`text ${item.is_used ? 'text-disable' : 'text-able'}`">
					{{ formatNumber(Number(item.amount) || 0) }}
				</div>
			</template>
			<template v-slot:[`item.markup_perc`]="{ item }">
				<div class="numeric-cell" :class="`text ${item.is_used ? 'text-disable' : 'text-able'}`">
					{{ formatNumber(Number(item.markup_perc) || 0) }} %
				</div>
			</template>
			<template v-slot:[`item.markup_value`]="{ item }">
				<div class="numeric-cell" :class="`text ${item.is_used ? 'text-disable' : 'text-able'}`">
					{{ formatNumber(Number(item.markup_value) || 0) }}
				</div>
			</template>
			<template v-slot:[`item.code`]="{ item }">
				<JobDropdown
					v-if="edit_control.edit_code_visible === item.id"
					:color="color"
					:outlined="true"
					:placeholder="`Type to search`"
					:jobSelected="item.title"
					@job-selected="onUpdate(edit_type.code, item.id, $event)"
					:style="$vuetify.breakpoint.smAndDown? 'text-align: left; width: 100%':'width: 120px'"
				/>				
				<div
					v-else
					:class="`text ${item.is_used ? 'text-disable' : item.code ? (privileges.includes(sec)?'text-able text-box-black':'text-able'):(privileges.includes(sec)?'text-able text-box-red':'text-able')}`" 
					@click.stop="onEditVisible(edit_type.code, item)" 
					v-html="item.code && item.code.code" >
				</div>
			</template>
			<template v-slot:[`item.title`]="{ item }">	
				<JobDropdown
					v-if="edit_control.edit_title_visible === item.id"
					:elementKey="'01'"
					:is_title="true"
					:color="color"
					:outlined="true"
					:placeholder="`Type to search`"
					:jobSelected="item.title"
					@job-selected="onUpdate(edit_type.title, item.id, $event)"
					:style="$vuetify.breakpoint.smAndDown? 'text-align: left; width: 100%':'width: 180px'"
				/>		
				<div
					v-else
					:class="`text ${item.is_used ? 'text-disable' : item.title ?(privileges.includes(sec)? 'text-able text-box-black':'text-able'):(privileges.includes(sec)?'text-able text-box-red':'text-able')}`" 
					@click.stop="onEditVisible(edit_type.title, item)" 
					v-html="item.title && item.title.display_name" >
				</div>
			</template>
			<template  v-slot:[`item.phase`]="{ item }">
				<v-select
					v-if="edit_control.edit_phase_visible === item.id"
					class="phase-select-cell"
					v-model="item.phase"
					:items="item.phases"
					item-text="display_name"
					item-value="id"
					dense
					hide-details
					outlined
					:color="color"
					:item-color="color"
					no-data-text="please select the job first"
					@change="onUpdate(edit_type.phase, item.id, $event)"
					@click.stop
				/>
				<div 
					v-else
					:class="`text ${item.is_used ? 'text-disable' : item.phase ?(privileges.includes(sec)? 'text-able text-box-black':'text-able'):(privileges.includes(sec)? 'text-able text-box-red':'text-able')}`"  
					@click.stop="onEditVisible(edit_type.phase, item)">
					{{ item.phase ? item.phase.display_name : '' }}
				</div>
			</template>
			<template v-slot:[`item.kind`]="{ item }">	
				<KindTreeSelection
					v-if="edit_control.edit_kind_visible === item.id"
					:color="color"
					:outlined="true"
					:kindParams="onKindParams(item.code)"
					:kindSelected="item.kind"
					@kind-selected="onUpdate(edit_type.kind, item.id, $event)"
					:style="$vuetify.breakpoint.smAndDown? 'text-align: left; width: 100%':'width: 180px'"
				/>
				<div 
					v-else
					class="text"
					:class="getClass(item)" 
					@click.stop="onEditVisible(edit_type.kind, item)">
					{{ item.kind ? item.kind.display_name : '' }}
				</div>
			</template>
			<template  v-slot:[`item.description`]="{ item }">
				<v-text-field
					v-if="edit_control.edit_desc_visible === item.id"
					v-model="item.description"
					:color="color"
					outlined
					hide-details
					dense
					background-color="#FFFFFF"
					@change="onUpdate(edit_type.desc, item.id, $event)"
					@click.stop
				></v-text-field>
				<div 
					v-else
					:class="`text ${item.is_used ? 'text text-disable' : item.description.length !== 0 ?(privileges.includes(sec)?'text-able text-box-black':'text-able'):(privileges.includes(sec)?'text-able text-box-red':'text-able')}`"  
					@click.stop="onEditVisible(edit_type.desc, item)">
					{{ item.description }}
				</div>
			</template>
			<template v-slot:[`item.thumbnail`]="{ item }">	
				<File 
					v-if="image_type.includes(item.image_type)"
					width="24" 
					hight="24" 
					:class="item.is_used ? 'ma-3': 'file-check ma-3'"  
					:color="item.is_used ? '#DADADA' : 'rgba(25, 42, 62, 0.8)'"
					@click="onShowFile(item)"/>
				<PDF
					v-if="item.image_type === pdf_type" 
					width="24" 
					hight="24" 
					:class="item.is_used ? 'ma-3': 'file-check ma-3'"  
					:color="item.is_used ? '#DADADA' : 'rgba(25, 42, 62, 0.8)'"
					@click="onShowFile(item)"/>
			</template>
			<template v-slot:[`item.is_used`]="{ item }">
				<span :class="item.is_used ? 'text-disable' : 'text-able'" v-if="item.is_used === true">Added to claim</span>
				<span :class="item.is_used ? 'text-disable' : 'text-able'" v-if="item.is_used === false">Not added to claim</span>		
			</template>
		</v-data-table>
		<div class="pagination">
			<v-pagination
				v-model="page"
				:color="color"
				:length="getPaginationTotal()"
				:total-visible="$vuetify.breakpoint.xs ? 3: 5"
				circle
			></v-pagination>
			<div class="pagination-text">show</div>
			<div class="pagination-text-page">{{ countPerpage}}/page</div>
			<v-menu offset-y>
				<template v-slot:activator="{ on, attrs }">
					<div v-bind="attrs" v-on="on"><Down class="down"/></div>
				</template>
				<v-list>
					<v-list-item-title v-for="(pageOption, index) in pageOptions" :key="index" class="option-text" @click="onClickOption(pageOption)">
						{{pageOption}}
					</v-list-item-title>
				</v-list>
			</v-menu>
		</div>
	</div>
</template>
<script>
import SortUpDown from '@/components/icons/sort_up_down';
import ShowSearch from '@/components/icons/show_search';
import File from '@/components/icons/attached_file_image';
import PDF from '@/components/icons/attached_file_pdf';
import FirstList from "@/components/icons/first_list";
import Down from '@/components/icons/down';
import KindTreeSelection from "@/components/kinds/kind_tree_selection";
import JobDropdown from "@/components/expenses/job_dropdown";
import DatePicker from "@/components/date/date_picker.vue";

import config from "@/config.js";
import moment from 'moment';

import * as pdfjsLib from 'pdfjs-dist/es5/build/pdf';
import pdfjsworker from 'pdfjs-dist/es5/build/pdf.worker.entry';
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsworker;


const FONT_COLOR = '#00AAFD';
const Max_Distance_Mobile = 48;
const Max_Distance_Window = 68;
const SCALE = 1.2;
const PDF_TYPE = 'application/pdf';
const IMAGE_TYPE = ['image/jpeg','image/png'];
const PAGE_INIT = 0;
const SEC = 'Sec';

export default {
	name: 'ReceiptTable',
	props: {
		color: {
			type: String,
			default: ""
		},
		currencies: {
			type: Array,
			default: () => [],
		},
		edit_type: {
			type: Object,
			default: () => {},
		},
		header: {
			type: Array,
			default: () => []
		},
		items: {
			type: Array,
			default: () => []
		},
		titleText: {
			type: String,
			default: ''
		},
		edit_control: {
			type: Object,
			default: () => {}
		},
		privileges: {
			type: Array,
			default: () => []
		}
	},
	components: {
		SortUpDown, 
		ShowSearch,
		File,
		PDF,
		FirstList,
		Down,
		KindTreeSelection,
		JobDropdown,
		DatePicker
	},
	data() {
		this.sec = SEC;
		return {
			headers_local: this.header,
			items_local: this.items,
			items_local_original: this.items,
			page: 1,
			countPerpage: 10,
			pageOptions: config.pageOptions,
			searchValue: '',
			sortByAscent: false,
			isHover: '',
			current_key: '',
			select_currency: this.currencies.map(currency => { return currency.id }),
			pdf_type: PDF_TYPE,
			image_type: IMAGE_TYPE
		}
	},
	watch: {
		header() {
			this.headers_local = this.header;
		},
		items() {
			this.items_local = this.items;
			this.items_local_original = this.items;
		},
		select_currency() {
			this.items_local = this.items_local_original.filter(item => {
				return this.select_currency.includes(item.currency.id);
			});
		},
		items_local: {
			handler(newVal) {
				const arr_tmp = newVal.filter(val => { return !val.is_used});
				const arr_select = arr_tmp.map(val => { return val.select});
				const control = arr_select.some(val => {return val === true});

				this.$emit('button-visible', control);
			},
			deep: true
		}
	},
	computed: {
		currentPageItems() {
			const start = (this.page - 1) * this.countPerpage;

			return this.items_local.slice(start, start + this.countPerpage);
		},
		currentPageSelectableItems() {
			return this.currentPageItems.filter(item => { return item.is_used !== true });
		},
		indeterminate() {
			const arr_tmp = this.currentPageSelectableItems;

			return arr_tmp.length !== 0 
				&& !arr_tmp.every(res => {return res.select === true})
				&& arr_tmp.some(res => { return res.select === true});
		},
		allSelect() {
			const arr_tmp = this.currentPageSelectableItems;

			return arr_tmp.length !== 0 &&
				arr_tmp.every(res => { return res.select === true});
		},
	},
	methods: {
		getClass(item) {
			let class_tmp = '';
			if (item.is_used) {
				class_tmp = 'text-disable';
			} else {
				if (this.privileges.includes(this.sec)) {
					if (item.kind) {
						if (this.validateKind(item.code, item.kind)) {
							class_tmp = 'text-able text-box-black';
						} else {
							class_tmp = 'text-error text-box-red';
						}
					} else {
						class_tmp = 'text-able text-box-red';
					}
				} else {
					class_tmp = 'text-able';
				}
			}

			return class_tmp;
		},
		onRowClick() {
			this.$emit('row-click');
		},
		onEditVisible(type, item) {
			if (!item.is_used) {
				const obj = {
					type: type,
					id: item.id
				};
				this.$emit('edit-visible', obj);
			}
		},
		async onUpdate(type, id, value) {
			if (type === this.edit_type.date && !value) return;

			const update_data = this.items_local.find(item => { return item.id === id });

			const obj = {
				id: update_data.id,
				employee_id: update_data.employee_id,
				desc: update_data.description ? update_data.description : '',
				date: update_data.date,
				currency_id: update_data.currency ? update_data.currency.id : null,
				value: update_data.value,
				has_tax: update_data.has_tax, /* seems it doesn't support boolean here */
				job_id: update_data.code ? update_data.code.id : null,
				phase_id: update_data.phase ? update_data.phase.id : null,
				kind_id: update_data.kind ? update_data.kind.id : null,
				tax_mode: update_data.tax_mode,
				tax_perc: Number(update_data.tax_perc) || 0,
				tax_amount: Number((Number(update_data.tax_amount) || 0).toFixed(2)),
				markup_perc: Number(update_data.markup_perc) || 0,
				markup_value: Number(update_data.markup_value)
			}
					
			if (type === this.edit_type.date) {
				obj.date = value;	
			}
			if (type === this.edit_type.currency) {
				obj.currency_id = value;

				const currency = this.currencies.find(c => { return c.id === value });
				const countries = currency.countries || [];
				const country = countries.find(c => { return update_data.job && update_data.job.country && c.id === this.job.country.id }) || countries[0];
				if (!country) return;

				const has_tax = country.has_tax;
				const tax_perc = Number(country.tax_perc);
				const tax_amount = has_tax ? Number(update_data.value) * tax_perc * 0.01 : 0;
				obj.tax_perc = tax_perc;
				obj.tax_amount = tax_amount;
				obj.has_tax = has_tax;
				obj.amount = Number(update_data.value) + tax_amount;
			}
			if (type === this.edit_type.value) {
				obj.value = Number(value);

				const tax_perc = Number(update_data.tax_perc);
				const tax_amount = update_data.has_tax ? Number(value) * tax_perc * 0.01 : 0;
				obj.tax_amount = tax_amount;
				obj.amount =  Number(value) + tax_amount;

				const markup_value = Number(value) + (Number(value) * Number(update_data.markup_perc) * 0.01);
				obj.markup_value = markup_value;
			}
			if (type === this.edit_type.kind) {
				if (!value) return;
				obj.kind_id = value.id;
			}
			if (type === this.edit_type.code) {
				obj.job_id = value.id;

				await this._getPhase(value.id)
					.then(data => {
						update_data.phases = data;
						/** The first phase in the list is the default selection */
						if (data.length !== 0) {
							update_data.phase = data[0];
							obj.phase_id = data[0].id
						} else {
							update_data.phase = null;
							obj.phase_id = null;
						}
					})
					.catch((reason) => {
						this.error_msg = reason;
					});

				if (obj.job_id && obj.kind_id && obj.phase_id) {
					await this._checkKind(obj.job_id, obj.kind_id, obj.phase_id)
						.then(val => {
							if (!val) {
								obj.kind_id = null;
							}
						})
						.catch((reason) => {
							this.error_msg = reason;
						});
				}
			}
			if (type === this.edit_type.title) {
				obj.job_id = value.id;

				await this._getPhase(value.id)
					.then(data => {
						update_data.phases = data;
						/** The first phase in the list is the default selection */
						if (data.length !== 0) {
							update_data.phase = data[0];
							obj.phase_id = data[0].id
						} else {
							update_data.phase = null;
							obj.phase_id = null;
						}
					})
					.catch((reason) => {
						this.error_msg = reason;
					});

				if (obj.job_id && obj.kind_id && obj.phase_id) {
					await this._checkKind(obj.job_id, obj.kind_id, obj.phase_id)
						.then(val => {
							if (!val) {
								obj.kind_id = null;
							}
						})
						.catch((reason) => {
							this.error_msg = reason;
						});
				}
			}
			if (type === this.edit_type.desc) {
				obj.desc = value;
			}
			if (type === this.edit_type.phase) {
				obj.phase_id = value;

				if (obj.job_id && obj.kind_id && obj.phase_id) {
					await this._checkKind(obj.job_id, obj.kind_id, obj.phase_id)
						.then(val => {
							if (!val) {
								obj.kind_id = null;
							}
						})
						.catch((reason) => {
							this.error_msg = reason;
						});
				}
			}

			const event = {
				type: type,
				obj: obj
			};
			this.$emit('update', event);
		},
		onSelectAllChange(value) {
			this.currentPageSelectableItems.forEach(item => {
				item.select = value;
			});
		},
		getPaginationTotal() {
			return Math.ceil(this.items_local.length / this.countPerpage);
		},
		onClickOption(option) {
			this.countPerpage = option;
		},
		onShowFile(item) {
			if (!item.is_used) {
				if (item.image_type === 'application/pdf') {
					const url = `/api/expenses/receipts/${item.id}/image`;
					this.getBlobDate(url);
				} else {
					const index = item.thumbnail.lastIndexOf('/');
					const image = item.thumbnail.slice(0, index + 1) + 'image';

					let image_width = 0;
					let image_height = 0;

					const my_image = new Image();
					my_image.src = image;
					my_image.onload = () => {
						if (this.$vuetify.breakpoint.smAndUp) {
							const max_height = this.$vuetify.breakpoint.height - Max_Distance_Window;
							image_width = max_height * (my_image.width / my_image.height);
							image_height = max_height;
						} else {
							/* 48 = padding-left-24px + padding-right-24px */
							const max_width = this.$vuetify.breakpoint.width - Max_Distance_Mobile;
							image_width = max_width;
							image_height = max_width * (my_image.height / my_image.width);
						}

						const show_image = {
							image: [image],
							image_width: image_width,
							image_height: image_height,
							url: image
						}

						this.$emit('show-file', show_image);
					}
				}
			}
		},
		onSort(key) {
			this.sortByAscent = !this.sortByAscent;

			const claimed_arr = this.items_local.filter(d => {return d.is_used});
			const other_arr = this.items_local.filter(d => {return !d.is_used});

			if (['value', 'tax_amount', 'amount', 'markup_perc', 'markup_value'].includes(key)) {
				this.sortByAscent
					? this._sort_ascent_number(claimed_arr, key)
					: this._sort_descent_number(claimed_arr, key);

				this.sortByAscent
					? this._sort_ascent_number(other_arr, key)
					: this._sort_descent_number(other_arr, key);

				const sorted_arr = [];
				sorted_arr.push(...other_arr);
				sorted_arr.push(...claimed_arr);

				this.items_local = sorted_arr;

			} else {
				this.sortByAscent
					? this._sort_ascent(claimed_arr, key)
					: this._sort_descent(claimed_arr, key);

				this.sortByAscent
					? this._sort_ascent(other_arr, key)
					: this._sort_descent(other_arr, key);

				const sorted_arr = [];
				sorted_arr.push(...other_arr);
				sorted_arr.push(...claimed_arr);

				this.items_local = sorted_arr;
			}	
		},
		onSearch(key) {
			if (this.searchValue.length === 0) return;

			this.current_key = key;
				
			const arr_filter = this._filterKey(this.items_local_original, key, this.searchValue);
			const arr_replace = JSON.parse(JSON.stringify(arr_filter));

			// to hightlight the kewwords
			const regex = new RegExp(this.searchValue, 'gi');
			arr_replace.forEach(arr => {
				if (arr[key]) {
					if (key === 'code') {
						arr[key].code = arr[key].code.replace(regex, (keyword) => {
							return `<font color="${FONT_COLOR}">${keyword}</font>`;
						});
					} else {
						if ( typeof(arr[key]) === 'object' ) {
							arr[key].display_name = arr[key].display_name.replace(regex, (keyword) => {
								return `<font color="${FONT_COLOR}">${keyword}</font>`;
							});
						} else {
							arr[key] = arr[key].replace(regex, (keyword) => {
								return `<font color="${FONT_COLOR}">${keyword}</font>`;
							});
						}
					}
				}
			});
			this.items_local = arr_replace;
		},
		onClean() {
			this.searchValue = '';
			this.items_local = this.items_local_original;
		},
		onKindParams(job) {
			const kind_params = {
				select_kinds: true,
				select_items: true,
				expense_kinds: true,
				overhead_mode: 0
			};

			if (job) {
				if (job.is_overhead === true) {
					kind_params.overhead_mode = 2;
				} else {
					kind_params.overhead_mode = 1;
				}
	
			}
			return kind_params;
		},
		formatDate(date) {
			if(!date) return null;
			return moment(date).format('DD MMM YYYY');
		},
		formatNumber(num) {
			let res = 0;
			if (Number.isInteger(num)) {
				const integer = num.toLocaleString();
				res = integer + '.00';
			} else {
				const num_arr = (num.toString()).split('.');
				const integer = Number(num_arr[0]).toLocaleString();

				if (num_arr.length > 1) {
					if (num_arr[1].length === 1) {
						res = integer + '.' + num_arr[1] + '0';
					} else {
						res = integer + '.' + num_arr[1];
					}
					
				} else {
					res = integer + '.00' ;
				}
			}
			return res;
		},
		validateKind(job, kind) {
			let validation = true;
			if (job) {
				// if (kind && kind.overhead_mode === 1 && !job.is_overhead) {
				// 	validation = false;
				// }
				if (kind && kind.overhead_mode === 0 && kind.show_over === 0 && job.is_overhead) {
					validation = false;
				}
			}

			return validation;
		},
		getBlobDate(url) {
			axios
				.get(url, {
					headers: {
						"Authorization": "Bearer " + this.$store.state.token,
						"Accept": "application/json"
					},
					responseType: 'blob',
				})
				.then((val) => {
					const { status, data } = val;
					if (status === 200) {
						const reader = new FileReader();
						reader.readAsDataURL(data); 
						reader.onload = readerEvent =>  {
							const loadingTask = pdfjsLib.getDocument(readerEvent.target.result);
							loadingTask.promise.then(pdf => {
								const preview_url = [];
								let page_total = pdf.numPages;

								for (let index = 0; index < pdf.numPages; index++) {
									pdf.getPage(index + 1).then(page => {
										const scale = SCALE;
										const viewport = page.getViewport({scale: scale});

										const canvas = document.createElement('canvas');
										const context = canvas.getContext('2d');
										canvas.width = viewport.width;
										canvas.height = viewport.height;

										const renderContext = {
											canvasContext: context,
											viewport: viewport
										};

										const renderTask = page.render(renderContext);
										renderTask.promise.then(() => {
											canvas.toBlob(blob => {
												const preview_item_url = URL.createObjectURL(blob);
												preview_url.push(preview_item_url);
												page_total -= 1;

												if (page_total === PAGE_INIT) {
													let image_width = 0;
													let image_height = 0;

													const isMobile = window.innerWidth < 600
													if (isMobile) {
														/** this.$vuetify.breakpoint.width <= 600 */
														const max_width = this.$vuetify.breakpoint.width - Max_Distance_Mobile;
														image_width = max_width;
														image_height = max_width * (viewport.height / viewport.width);

													} else {
														/** this.$vuetify.breakpoint.width >= 960 */
														const max_height = this.$vuetify.breakpoint.height - Max_Distance_Window;
														image_width = max_height * (viewport.width / viewport.height);
														image_height = max_height;
													}

													const show_image = {
														image: preview_url,
														image_width: image_width,
														image_height: image_height,
														url: url
													};
													this.$emit('show-file', show_image);
												}
											});									
										});
									});
								}
							});
						};
					}
				})
				.catch((e) => {
					this.$emit('error-msg', e.message);
				});
		},
		_getPhase(job_id) {
			const url = `/api/jobs/${job_id}/phases`;

			return new Promise((resolve, reject) => {
				axios
					.get(url, {
						headers: {
							"Authorization": "Bearer " + this.$store.state.token,
							"Accept": "application/json"
						}
					})
					.then((val) => {
						const json = val.data;

						const { status, data, reason } = json;

						if (status == "OK") {
							resolve(data);
						} else {
							reject(reason);
						}
					})
					.catch((e) => {
						reject(e.message);
					});
			});	
		},
		_checkKind(job_id, kind_id, phase_id) {
			let url = `/api/jobs/${job_id}/can_do_kind`;

			const params = [];
			params.push("kiid=" + kind_id);
			params.push("jpid=" + phase_id);


			if (params.length > 0) {
				url+= "?" + params.join('&');
			}

			return new Promise((resolve, reject) => {
				axios
					.get(url, {
						headers: {
							"Authorization": "Bearer " + this.$store.state.token,
							"Accept": "application/json"
						}
					})
					.then((val) => {
						const json = val.data;

						const { status, data, reason } = json;

						if (status == "OK") {
							if (data.can_do_client && data.can_do_jobtype) {
								resolve(true);
							} else {
								resolve(false);
							}
							
						} else {
							reject(reason);
						}
					})
					.catch((e) => {
						reject(e.message);
					});
			});	
		},
		_sort_ascent(arr, key) {
			arr.sort((a, b) => {
				let valueA = '';
				let valueB = '';

				if (key === 'code') {
					if (a[key]) {
						valueA = typeof(a[key]) === 'object' ? a[key].code.toUpperCase() : a[key].toUpperCase();
					}
					if (b[key]) {
						valueB = typeof(b[key]) === 'object' ? b[key].code.toUpperCase() : b[key].toUpperCase();
					}
				}	else {
					if (a[key]) {
						valueA = typeof(a[key]) === 'object' ? a[key].display_name.toUpperCase() : a[key].toUpperCase();
					}
					if (b[key]) {
						valueB = typeof(b[key]) === 'object' ? b[key].display_name.toUpperCase() : b[key].toUpperCase();
					}
				}

				if (valueA < valueB) {
					return -1;
				}
				if (valueA > valueB) {
					return 1;
				}

				return 0;
			});
		},
		_sort_descent(arr, key) {
			arr.sort((a, b) => {
				let valueA = '';
				let valueB = '';

				if (key === 'code') {
					if (a[key]) {
						valueA = typeof(a[key]) === 'object' ? a[key].code.toUpperCase() : a[key].toUpperCase();
					}
					if (b[key]) {
						valueB = typeof(b[key]) === 'object' ? b[key].code.toUpperCase() : b[key].toUpperCase();
					}
				} else {
					if (a[key]) {
						valueA = typeof(a[key]) === 'object' ? a[key].display_name.toUpperCase() : a[key].toUpperCase();
					}
					if (b[key]) {
						valueB = typeof(b[key]) === 'object' ? b[key].display_name.toUpperCase() : b[key].toUpperCase();
					}
				}

				if (valueA > valueB) {
					return -1;
				}
				if (valueA < valueB) {
					return 1;
				}

				return 0;
			});
		},
		_sort_ascent_number(arr, key) {
			arr.sort((a, b) => {
				return a[key] - b[key];
			});
		},
		_sort_descent_number(arr, key) {
			arr.sort((a, b) => {
				return b[key] - a[key];
			});
		},
		_filterKey(arr, key, filter) {
			let fliter_Arr = [];
			const filter_UpperCase = filter.toUpperCase();
			fliter_Arr = arr.filter(a => {
				let A = '';
				if (a[key]) {
					if (key === 'code') {
						A = typeof(a[key]) === 'object' ? a[key].code.toUpperCase() : a[key].toUpperCase();
					} else {
						A = typeof(a[key]) === 'object' ? a[key].display_name.toUpperCase() : a[key].toUpperCase();
					}
				} 
				return A.includes(filter_UpperCase);
			});
			
			return fliter_Arr;
		},
	}
}
</script>
<style lang="scss" scoped>
::v-deep .receipt-table > .v-data-table__wrapper > table {
	width: max-content;
	min-width: 100%;
	table-layout: auto;
}
.numeric-cell {
	width: 100%;
	min-width: 80px;
	text-align: right;
	white-space: nowrap;
}
.amount-input-cell {
	width: 80px;
	min-width: 80px;
	max-width: 80px;
}
.date-picker-cell {
	width: 132px;
	min-width: 132px;
	max-width: 132px;
}
.currency-select-cell {
	width: 60px;
	min-width: 60px;
	max-width: 60px;
}
.phase-select-cell {
	width: 100px;
	min-width: 100px;
	max-width: 100px;
}
::v-deep .currency-select-cell .v-input__slot,
::v-deep .currency-select-cell .v-select__selections {
	height: 36px;
	min-height: 36px;
}
::v-deep .currency-select-cell .v-select__selections {
	flex-wrap: nowrap;
}
::v-deep .currency-select-cell .v-select__selection {
	margin-top: 0;
	margin-bottom: 0;
}
::v-deep .date-picker-cell input {
	font-size: 14px;
}
::v-deep .date-picker-cell .v-text-field--outlined.v-input--dense .v-input__control .v-input__slot {
	min-height: 36px;
}
::v-deep .date-picker-cell .v-input__icon--clear .v-icon {
	font-size: 20px;
}
::v-deep .date-picker-cell .v-text-field--outlined.v-input--dense .v-input__append-inner {
	margin-top: 6px;
}
.table-head {
	display: flex;
	flex-flow: row nowrap;
	justify-content: flex-start;
	align-items: center;
	height: 48px;
	padding: 0 20px;
	background: #FAFAFA;
	color: #192A3E;
	font-family: Roboto;
	font-style: normal;
	font-weight: normal;
	font-size: 16px;
	line-height: 24px;
	border-bottom: 1px solid #C9C9C9;
	border-radius: 4px 4px 0 0;
	span {
		font-weight: bold;
	}
}
.header-th {
	background: #FAFAFA;
	padding-right: 12px !important;
	padding-left: 12px !important;
	&:hover {
		background: #EAEAEA;
		cursor: move;
	}
}
.header-th:first-child {
	padding-left: 16px !important;
}
.header-item {
	display: flex;
  flex-flow: row nowrap;
	justify-content: space-between;
  align-items: center;
}
.header-item-sub {
	display: flex;
  flex-flow: row nowrap;
	justify-content: flex-start;
  align-items: center;
}
.header-th:hover .icon {
	visibility: visible; 
}
.header-item-text {
	white-space: nowrap;
	text-overflow: ellipsis;
}
.icon-wrap {
	display: flex;
	flex-flow: row nowrap;
	justify-content: center;
	align-items: center;
}
.icon {
	visibility: hidden;
	width: 14px;
  height: 14px;
	&:hover {
		cursor: pointer;
	}
}
.icon-sort-up-down {
  margin-left: 6px;
}
.search-box {
	padding: 13px;
	display: flex;
	flex-flow: column nowrap;
	justify-content: center;
	align-items: center;
	background: #FFFFFF;
	border-radius: 4px;
	.search-box-button {
		margin-top: 9px;
		display: flex;
		flex-flow: row nowrap;
		justify-content: space-between;
		align-items: center;
		div {
			display: flex;
			flex-flow: row nowrap;
			justify-content: center;
			align-items: center;
			border-radius: 4px;
			padding: 0 21px;

			font-family: Roboto;
			font-style: normal;
			font-weight: 500;
			font-size: 14px;
			height: 36px;

			&:hover {
				cursor: pointer;
			}
		}
		:first-child {
			border: 1px solid rgba(25, 42, 62, 0.7);
			margin-right: 10px;
			color: rgba(25, 42, 62, 0.7);
		}
		:last-child {
			border: 1px solid #00AAFD;
			background: #00AAFD;
			color: #FFFFFF;
		}
	}
}
.filter-box {
	background: #FFFFFF;
}
.text {
	padding: 8px 12px;
}
.text-box-black {
	&:hover {
		cursor: pointer;
		box-shadow: inset 0 0 0 1px rgba(25, 42, 62, 0.3);
		border-radius: 4px;
	}
}
.text-box-red {
	box-sizing: border-box;
	min-height: 36px;

	&:hover {
		cursor: pointer;
		box-shadow: inset 0 0 0 1px #F45252;
		border-radius: 4px;
	}
}
.text-able {
	color: rgba(0, 0, 0, 0.87);
}
.text-disable {
	color: #C4C4C4;
}
.text-error {
	color: #F45252;
}
.file-check {
	&:hover {
		cursor: pointer;
	}
}
.pagination {
	display: flex;
	flex-flow: row nowrap;
	justify-content: flex-start;
	align-items: center;
	.pagination-text {
		font-family: Roboto;
		font-style: normal;
		font-weight: normal;
		font-size: 13px;
		line-height: 16px;
		color: rgba(25, 42, 62, 0.5);
	}
	.pagination-text-page {
		font-family: Roboto;
		font-style: normal;
		font-weight: normal;
		font-size: 13px;
		line-height: 16px;
		color: #192A3E;
		margin: 0 8px;
	}
	.down {
		width: 12px;
		height: 12px;
		margin-top: 10px;
		&:hover {
			cursor: pointer;
		}
	}
	
}
::v-deep {
	.option-text {
		margin: 0 4px;
		font-family: Roboto;
		font-style: normal;
		font-weight: normal;
		font-size: 13px;
		color: #192A3E;
		&:hover {
			cursor: pointer;
		}
	}
	.v-data-table {
		border-radius: 4px;
		border: 1px solid #C9C9C9;
	}
}
</style>
